<template>
  <div class="brb-uploader flex-grow-1">
    <brb-input-group
      :size="size"
      :prepend="label"
      :required="required"
      v-bind="$attrs"
    >
      <template v-if="!!$slots.prepend" #prepend>
        <slot name="prepend"></slot>
      </template>

      <template #append>
        <b-btn
          ref="brb-uploader-browse-button"
          class="d-flex align-items-center justify-content-center p-2"
          style="border-top-left-radius: 5px; border-bottom-left-radius: 5px"
          :variant="uploading ? 'secondary' : 'success'"
          :size="size"
          :disabled="inputDisabled"
          :class="{ 'invalid-input': error }"
          :tab-index="tabIndex"
          @click="browse"
        >
          <b-spinner v-if="uploading" small />

          <template v-else>
            <font-awesome-icon
              icon="cloud-upload-alt"
              :class="`m${$dir.rtl ? 'l' : 'r'}-sm-2`"
            />

            <span class="d-none d-sm-block">
              {{ $t('forms.ChooseFile') }}
            </span>
          </template>
        </b-btn>
      </template>

      <div class="input flex-grow-1 px-2">
        <template v-if="files && files.length">
          <div
            v-for="(file, index) in files"
            :key="file"
            class="d-flex flex-grow-1 align-items-center py-1"
          >
            <span
              v-if="multiple"
              class="d-block text-muted font-weight-bold font-lg single-line text-center"
              style="width: 20px"
            >
              {{ index + 1 }}
            </span>

            <div class="flex-grow-1 mx-2">
              <brb-image-preview
                v-if="file.match(/\.(jpeg|jpg|gif|png)$/) != null"
                :src="file"
                width="40"
                height="40"
                no-thumbnail
                circle
              />

              <div
                v-else
                class="d-flex align-items-center justify-content-center border rounded-circle"
                style="width: 40px; height: 40px"
              >
                <font-awesome-icon icon="file" size="2x" class="text-muted" />
              </div>
            </div>

            <div class="d-flex align-items-center">
              <b-btn
                :href="$config.files.createStaticUrl(file)"
                size="sm"
                target="_blank"
                class="d-flex align-items-center justify-content-center"
              >
                <font-awesome-icon
                  icon="eye"
                  :class="`m${$dir.rtl ? 'l' : 'r'}-2`"
                />
                <span class="small">{{ $t('shared.View') }}</span>
              </b-btn>

              <b-btn
                variant="danger"
                size="sm"
                :class="[
                  'd-flex align-items-center justify-content-center',
                  `m${$dir.rtl ? 'r' : 'l'}-2`
                ]"
                @click="remove(file)"
              >
                <font-awesome-icon
                  icon="trash"
                  :class="`m${$dir.rtl ? 'l' : 'r'}-2`"
                />
                <span class="small">{{ $t('shared.Remove') }}</span>
              </b-btn>
            </div>
          </div>
        </template>

        <div
          v-else-if="!uploading"
          class="d-flex h-100 align-items-center justify-content-center"
        >
          <span :class="['text-muted', { small: size === 'sm' }]">
            موردی انتخاب نشده
          </span>
        </div>

        <b-progress
          v-if="uploading"
          variant="success"
          class="mb-1"
          :value="progress"
          show-progress
          animated
        />
      </div>

      <input
        v-show="false"
        ref="brb-uploader-file-input"
        :accept="accept"
        :disabled="inputDisabled"
        :multiple="multiple"
        type="file"
        @change="onInputChange"
      />
    </brb-input-group>

    <span
      v-if="!noError && error && typeof error === 'string'"
      class="d-block text-right text-danger small mt-2"
    >
      {{ error }}
    </span>

    <template v-if="interlanErrors && interlanErrors.length">
      <ul class="pr-3 mt-2">
        <li
          v-for="error in interlanErrors"
          :key="error"
          class="small text-danger"
        >
          {{ error }}
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import { scaleImage } from '@/lib/images'
import { IMAGE_FORMATS } from '@/constants/file-formats'
import { getExtension } from '@/utils/file-helper'

export default {
  name: 'BrbUploader',
  components: {
    BrbInputGroup: () => import('./BrbInputGroup.vue')
  },
  props: {
    id: {
      type: String
    },
    value: {
      default: null
    },
    accept: {
      type: String,
      default: '.png,.jpg,.jpeg'
    },
    maxSize: {
      type: Number,
      default: 1073741824 // 1073741824 bytes = (1 Gig)
    },
    maxUploadCount: {
      type: Number,
      default: 20
    },
    autoScale: {
      // pixels
      type: Number,
      default: null
    },
    documentUploadModel: {
      type: Object,
      validator: (value) =>
        value === null ||
        (Object.prototype.hasOwnProperty.call(value, 'api') &&
          Object.prototype.hasOwnProperty.call(value, 'scope') &&
          Object.prototype.hasOwnProperty.call(value, 'path')),
      default: null
    },
    label: {
      required: false
    },
    size: {
      type: String,
      default: 'md'
    },
    inputClass: {
      type: [String, Object, Array]
    },
    error: {
      type: [Boolean, String]
    },
    inputGroupClass: {
      type: [String, Array, Object],
      default: null
    },
    inputGroupStyle: {
      type: [String, Array, Object],
      default: null
    },
    multiple: Boolean,
    square: Boolean,
    tabIndex: String,
    required: Boolean,
    disabled: Boolean,
    noError: Boolean
  },
  data() {
    return {
      interlanErrors: [],
      progress: 0
    }
  },
  computed: {
    files: {
      get() {
        return this.value ? this.value.split(',') : []
      },
      set(value) {
        console.log(value)
        this.$emit('input', value && value.length ? value.toString() : null)
      }
    },
    acceptArray() {
      return this.accept && this.accept !== '*.*'
        ? this.accept
            .split(',')
            .map((extention) => extention.replace('.', '').toLowerCase())
        : []
    },
    uploading() {
      return this.progress > 0
    },
    inputDisabled() {
      return (
        this.disabled ||
        this.uploading ||
        this.files.length >= this.maxUploadCount
      )
    }
  },
  watch: {
    accept(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.files.forEach((file) => {
          this.checkExtension(file)
        })
      }
    },
    value(newValue, oldValue) {
      if (!oldValue) this.files.forEach((file) => this.checkExtension(file))
      if (!newValue) this.interlanErrors = []
    }
  },
  methods: {
    browse() {
      this.$refs['brb-uploader-file-input'].click()
    },
    onInputChange(event) {
      this.interlanErrors = []
      if (this.square) {
        const files = Array.from(event.target.files)
        const checkArray = files.map((file) => this.checkSquareImage(file))

        return Promise.all(checkArray)
          .then((filesToUpload) => this.upload(filesToUpload))
          .catch(() => {
            this.$showError(
              this.$t(
                `forms.squareImageError.${
                  this.multiple ? 'multiple' : 'single'
                }`
              )
            )
          })
      } else {
        this.upload(Array.from(event.target.files))
      }
    },
    upload(files) {
      if (this.files.length + files.length > this.maxUploadCount) {
        this.$showError(
          this.$t('forms.uploadCountLimit', { count: this.maxUploadCount })
        )
        return
      }
      return this.validateFiles(files).then((files) => {
        let promise = null
        if (this.documentUploadModel) {
          promise = this.documentUploadModel.api
            .upload(
              this.documentUploadModel.scope,
              this.documentUploadModel.path,
              files,
              this.onProgress,
              true
            )
            .then((response) => {
              response.forEach((file) => {
                this.files.push(file.url.substring(1))
              })

              return response
            })
        } else {
          promise = this.$api.files
            .upload(files, this.onProgress, true)
            .then((response) => {
              this.files = this.multiple
                ? this.files.concat(response)
                : [response]

              return response
            })
        }

        promise
          .then(() => {
            this.onChange()
            this.progress = 0
            this.clearInput()
          })
          .catch((error) => {
            this.interlanErrors.push(error)
            this.progress = 0
            this.clearInput()
          })
      })
    },
    checkSquareImage(file) {
      const fileExtention = getExtension(file.name)
      return new Promise((resolve, reject) => {
        if (IMAGE_FORMATS.includes(fileExtention)) {
          const img = new Image()
          img.onload = function () {
            if (this.width === this.height) {
              return resolve(file)
            } else {
              return reject()
            }
          }

          img.src = window.URL.createObjectURL(file)
        } else {
          return resolve(file)
        }
      })
    },
    validateFiles(files) {
      return new Promise((resolve) => {
        const acceptedFiles = []

        files.forEach((file) => {
          if (this.checkExtension(file.name)) {
            if (file.size > this.maxSize) {
              this.interlanErrors.push(
                this.$t('forms.File_max_size_exceeded', {
                  fileName: file.name,
                  maxSize: Math.floor(this.maxSize / 1024)
                })
              )
            } else {
              acceptedFiles.push(file)
            }
          }
        })

        if (acceptedFiles.length && this.autoScale) {
          const scalePromiseArray = []
          acceptedFiles.forEach((file) => {
            scalePromiseArray.push(
              file.type.match(/image.*/)
                ? scaleImage(file, this.autoScale)
                : file
            )
          })

          return Promise.all(scalePromiseArray).then((results) =>
            resolve(results)
          )
        } else {
          return resolve(acceptedFiles)
        }
      })
    },
    checkExtension(fileName) {
      if (this.accept === '*.*') return true
      const fileExtention = getExtension(fileName)
      const valid = this.acceptArray.includes(fileExtention)
      if (!valid) {
        this.interlanErrors.push(
          `${fileExtention}     ${this.$t('forms.File_extension_not_accepted', {
            fileName: fileName,
            accepted: this.accept
          })}`
        )
      }
      return valid
    },
    remove(file, showConfirm = true) {
      if (showConfirm) {
        return this.$showConfirm(
          null,
          this.$t('forms.AreYouSureToRemoveThisFile')
        ).then(() => {
          return this.deleteFile(file)
        })
      } else {
        this.deleteFile(file)
      }
    },
    deleteAll() {
      if (this.files && this.files.length) {
        this.files.forEach((file) => {
          this.deleteFile(file)
        })
      }
    },
    deleteFile(file) {
      if (file.startsWith('/temp')) {
        const fileName = file.split('/').pop()
        this.$api.files.delete(fileName, true)
      }

      this.files = this.files.filter((_file) => _file !== file)
      this.onChange()
    },
    onProgress(e) {
      this.progress = parseInt((e.loaded * 100) / e.total)
      this.$emit('progress', this.progress)
    },
    onChange() {
      this.$emit('change', this.files)
    },
    clearInput() {
      if (this.$refs['brb-uploader-file-input'])
        this.$refs['brb-uploader-file-input'].value = ''
    }
  }
}
</script>
